export const SESSION_APPS = Object.freeze([
  "dealership_web",
  "group_web",
  "developer_web",
]);

export const SESSION_PRODUCT_APPS = Object.freeze([
  ...SESSION_APPS,
  "reseller_app",
]);

const PRODUCT_LABELS = Object.freeze({
  dealership_web: "Concesionaria",
  group_web: "Grupo de concesionarias",
  developer_web: "Administración de AutoApp",
  reseller_app: "Reventa",
});

const PRODUCT_DESCRIPTIONS = Object.freeze({
  dealership_web: "Subastas, peritajes y operación de una concesionaria",
  group_web: "Administración y reportes de un grupo",
  developer_web: "Herramientas internas de administración",
  reseller_app: "Subastas disponibles para compradores",
});

export const isUsableSessionContext = (context) =>
  context?.success === "yes" &&
  ["selecting", "ready"].includes(context?.sessionState) &&
  context?.contextType !== "dealership_legacy";

/**
 * Un contexto sólo puede habilitar el producto pedido si el backend lo
 * identifica explícitamente como tal. Nunca se infiere desde role/type ni se
 * reutiliza una respuesta válida de otra aplicación.
 */
export const isContextForProduct = (context, requestedApp) =>
  SESSION_PRODUCT_APPS.includes(requestedApp) &&
  isUsableSessionContext(context) &&
  context?.activeApp === requestedApp;

/**
 * Proyección deliberadamente mínima para el selector. Los permisos y scopes
 * permanecen fuera de esta lista y se vuelven a obtener al elegir producto.
 */
export const buildProductChoices = (contexts = []) => {
  const contextsByApp = new Map(
    contexts
      .filter((context) =>
        isContextForProduct(context, context?.activeApp),
      )
      .map((context) => [context.activeApp, context]),
  );

  return SESSION_PRODUCT_APPS.filter((app) => contextsByApp.has(app)).map(
    (app) => ({
      app,
      label: PRODUCT_LABELS[app],
      description: PRODUCT_DESCRIPTIONS[app],
    }),
  );
};

export const getContextChoices = (context) => {
  if (
    context?.activeApp === "group_web" &&
    Array.isArray(context?.availableGroups)
  ) {
    return context.availableGroups;
  }
  if (Array.isArray(context?.availableUnits)) return context.availableUnits;
  if (Array.isArray(context?.availableGroups)) return context.availableGroups;
  return [];
};

export const getActiveContextId = (context) =>
  context?.activeUnitId || context?.activeGroupId || null;

export const getSelectionParameter = (activeApp, id) =>
  activeApp === "group_web"
    ? { activeGroupId: id }
    : { activeUnitId: id };

/**
 * Mantiene el punto único de adaptación de la respuesta de selección. El
 * estado es autoridad del backend: el cliente nunca promueve `selecting` a
 * `ready` ni recupera permisos desde la opción elegida.
 */
export const normalizeSelectedContext = (context, requestedId) => {
  if (context?.success !== "yes" || !requestedId) return context;
  const choices = getContextChoices(context);
  const selectedExists = choices.some((choice) => choice.id === requestedId);
  if (!selectedExists || getActiveContextId(context) !== requestedId) {
    return context;
  }
  return context;
};

const TYPE_BY_CONTEXT = Object.freeze({
  operating_unit: "dealership",
  group: "group",
  developer: "developer",
  reseller: "reseller",
});

const TYPE_BY_APP = Object.freeze({
  dealership_web: "dealership",
  group_web: "group",
  developer_web: "developer",
  reseller_app: "reseller",
});

const CONTEXT_BY_APP = Object.freeze({
  dealership_web: "operating_unit",
  group_web: "group",
  developer_web: "developer",
  reseller_app: "reseller",
});

/**
 * DTO aplanado para componentes todavía no migrados. No se usa para autorizar:
 * las decisiones dependen exclusivamente de activeApp + permissions.
 */
export const buildCompatibilityUser = (firebaseUser, context) => {
  if (context?.contextType === "dealership_legacy") return null;
  const choices = getContextChoices(context);
  const activeId = getActiveContextId(context);
  const selected = choices.find((choice) => choice.id === activeId) || null;
  const compatibility = context?.compatibility || context?.legacyProfile || {};
  const contextType =
    context?.contextType || CONTEXT_BY_APP[context?.activeApp] || null;
  const activeUnitId =
    contextType === "operating_unit" ? context?.activeUnitId || null : null;
  const type = TYPE_BY_CONTEXT[contextType] || TYPE_BY_APP[context?.activeApp] || null;
  const dealershipId = context?.dealershipId || selected?.dealershipId || null;
  const groupIds = Array.isArray(context?.groupIds)
    ? context.groupIds
    : context?.groupId
      ? Array.isArray(context.groupId)
        ? context.groupId
        : [context.groupId]
      : [];
  const groupId =
    context?.groupId ||
    context?.activeGroupId ||
    selected?.groupId ||
    (contextType === "reseller" ? groupIds : null);
  const displayName =
    context?.dealership ||
    compatibility.dealership ||
    selected?.displayName ||
    context?.displayName ||
    null;

  return {
    ...compatibility,
    success: "yes",
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    emailVerified: firebaseUser.emailVerified,
    contextType,
    sessionSource: context?.sessionSource || "v3",
    sessionState: context?.sessionState,
    activeApp: context?.activeApp || null,
    activeUnitId,
    activeGroupId: context?.activeGroupId || null,
    activeUnitName:
      context?.activeUnitDisplayName ||
      (activeUnitId && selected ? selected.displayName || selected.id : null),
    availableUnits: context?.availableUnits || [],
    availableGroups: context?.availableGroups || [],
    dealershipId,
    groupId,
    groupIds,
    role: context?.role || selected?.role || null,
    administrativeScope:
      context?.administrativeScope || selected?.administrativeScope || null,
    allowedApps:
      context?.allowedApps ||
      selected?.allowedApps ||
      (context?.activeApp ? [context.activeApp] : []),
    permissions: context?.permissions || selected?.permissions || [],
    grantablePermissions:
      context?.grantablePermissions || selected?.grantablePermissions || [],

    // Campos legacy requeridos durante la migración aditiva.
    type,
    id:
      context?.id ||
      (type === "group" ? groupId : type === "reseller" ? firebaseUser.email : dealershipId) ||
      firebaseUser.uid,
    dealership: displayName,
    groupName: context?.groupName || compatibility.groupName || selected?.displayName || null,
    pickupLocations:
      context?.pickupLocations || compatibility.pickupLocations || selected?.pickupLocations || [],
    appraisalCfg:
      context?.appraisalCfg ||
      compatibility.appraisalCfg ||
      selected?.appraisalCfg ||
      [],
    adminEmail: context?.adminEmail || compatibility.adminEmail || null,
    memberEmail: context?.memberEmail || compatibility.memberEmail || null,
    name:
      context?.memberDisplayName ||
      context?.name ||
      context?.displayName ||
      compatibility.name ||
      null,
    address:
      context?.address ||
      context?.dealershipAddress ||
      compatibility.address ||
      null,
    phone: context?.phone || compatibility.phone || null,
    city: context?.city || compatibility.city || null,
    bannedScopes: context?.bannedScopes || compatibility.bannedScopes || [],
    banned:
      context?.banned ||
      compatibility.banned ||
      context?.bannedScopes ||
      [],
    favoriteAuctions:
      context?.favoriteAuctions || compatibility.favoriteAuctions || [],
  };
};

export const getOperationalContextKey = (context, version = 0) =>
  [
    context?.activeApp || "none",
    getActiveContextId(context) || "none",
    version,
  ].join(":");
